/**
 * useFacetedFilters — Hook for faceted search filter options.
 *
 * When any filter changes, re-fetches ALL filter dropdown options with counts
 * reflecting the current filter state (standard faceted search behavior).
 *
 * Each dimension's counts exclude its OWN filter — so Brand dropdown still
 * shows all brands even when Brand=Samsung is active, but Site/Cat/Subcat/Seg
 * counts update to reflect Samsung-only.
 *
 * Performance:
 * - Single API call per filter change (all facets computed server-side)
 * - Debounced to avoid flooding on rapid filter changes
 * - AbortController cancels stale requests
 * - Falls back to empty facets on error (non-blocking)
 */
import { useState, useEffect, useRef, useCallback } from 'react';
import api from '../utils/api';

export interface FacetValues {
  values: { value: string; n: number }[];
  total: number;
  hasEmpty: boolean;
}

export interface FacetResults {
  brand?: FacetValues;
  site?: FacetValues;
  category?: FacetValues;
  subcategory?: FacetValues;
  segment?: FacetValues;
  _meta?: {
    elapsed_ms: number;
    catalogJoinUsed: boolean;
    dimensions: string[];
  };
}

interface FacetFilters {
  brand?: string;    // pipe-separated: "Samsung|LG"
  site?: string;
  category?: string;
  subcategory?: string;
  segment?: string;
}

interface UseFacetedFiltersOptions {
  tableName: string;
  enabled: boolean;       // master switch — disable to fall back to legacy
  dimensions?: string[];  // which facets to compute (default: all)
  debounceMs?: number;    // debounce delay (default: 300ms)
  includeHidden?: boolean; // Data Health cuenta ocultos; Publicaciones no (default: false)
}

const EMPTY_FACET: FacetValues = { values: [], total: 0, hasEmpty: false };

export function useFacetedFilters(
  filters: FacetFilters,
  options: UseFacetedFiltersOptions
) {
  const { tableName, enabled, dimensions, debounceMs = 300, includeHidden = false } = options;
  const [facets, setFacets] = useState<FacetResults>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Serialize filters for dependency tracking
  const filtersKey = JSON.stringify(filters);

  const fetchFacets = useCallback(async (signal: AbortSignal) => {
    if (!tableName || !enabled) return;

    setLoading(true);
    setError(null);

    try {
      const params: Record<string, string> = {
        filters: JSON.stringify(filters),
      };
      if (dimensions && dimensions.length > 0) {
        params.dimensions = dimensions.join(',');
      }
      if (includeHidden) {
        params.includeHidden = 'true';
      }

      const response = await api.get(`/api/tables/${tableName}/facets`, {
        params,
        signal,
      });

      if (!signal.aborted) {
        setFacets(response.data);
        setLoading(false);

        // Log slow queries for debugging
        if (response.data?._meta?.elapsed_ms > 2000) {
          console.warn(
            `[useFacetedFilters] Slow facet query: ${response.data._meta.elapsed_ms}ms`
          );
        }
      }
    } catch (err: any) {
      if (err?.name === 'CanceledError' || err?.code === 'ERR_CANCELED' || signal.aborted) {
        return; // Expected cancellation, don't set error
      }
      console.warn('[useFacetedFilters] Facet fetch failed:', err?.message);
      if (!signal.aborted) {
        setError(err?.message || 'Facet fetch failed');
        setLoading(false);
      }
    }
  }, [tableName, enabled, filtersKey, dimensions?.join(','), includeHidden]);

  // Debounced fetch on filter change
  useEffect(() => {
    if (!enabled || !tableName) {
      setFacets({});
      return;
    }

    // Cancel previous request
    if (abortRef.current) {
      abortRef.current.abort();
    }
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }

    const controller = new AbortController();
    abortRef.current = controller;

    timerRef.current = setTimeout(() => {
      fetchFacets(controller.signal);
    }, debounceMs);

    return () => {
      controller.abort();
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, [filtersKey, tableName, enabled, fetchFacets, debounceMs]);

  // Helper to get options for a specific dimension
  const getOptions = useCallback((dim: keyof FacetResults): string[] => {
    const facet = facets[dim] as FacetValues | undefined;
    if (!facet?.values) return [];
    return facet.values.map(v => v.value);
  }, [facets]);

  // Helper to get meta (counts + total) for a specific dimension
  const getMeta = useCallback((dim: keyof FacetResults): { counts: Record<string, number>; total: number } | null => {
    const facet = facets[dim] as FacetValues | undefined;
    if (!facet?.values) return null;
    const counts: Record<string, number> = {};
    for (const v of facet.values) {
      counts[v.value] = v.n;
    }
    return { counts, total: facet.total };
  }, [facets]);

  return {
    facets,
    loading,
    error,
    getOptions,
    getMeta,
    // Expose individual facets for convenience
    brandFacet: (facets.brand as FacetValues) || EMPTY_FACET,
    siteFacet: (facets.site as FacetValues) || EMPTY_FACET,
    categoryFacet: (facets.category as FacetValues) || EMPTY_FACET,
    subcategoryFacet: (facets.subcategory as FacetValues) || EMPTY_FACET,
    segmentFacet: (facets.segment as FacetValues) || EMPTY_FACET,
  };
}
